import React, { useEffect, useState, useCallback } from 'react';
import { useAppSelector } from '../../store/hooks';
import { fetchSupportTickets, updateSupportTicket } from '../../api/admin';
import { AdminDataTable, AdminPagination, Column } from '../../components/admin';

const statusOptions = [
  { label: 'All', value: '' },
  { label: 'Open', value: 'open' },
  { label: 'In Progress', value: 'in_progress' },
  { label: 'Resolved', value: 'resolved' },
  { label: 'Closed', value: 'closed' },
];

const AdminSupport: React.FC = () => {
  const colors = useAppSelector((state) => state.theme.theme);
  const [tickets, setTickets] = useState<any[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<any>(null);
  const [newStatus, setNewStatus] = useState('');
  const [adminNotes, setAdminNotes] = useState('');
  const pageSize = 20;

  const load = useCallback(() => {
    setLoading(true);
    const params: Record<string, string | number> = { page, page_size: pageSize };
    if (statusFilter) params.status = statusFilter;
    fetchSupportTickets(params)
      .then((res) => { setTickets(res.data.results); setTotal(res.data.total); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [page, statusFilter]);

  useEffect(() => { load(); }, [load]);

  const handleUpdate = async () => {
    if (!selected) return;
    const data: Record<string, any> = {};
    if (newStatus) data.status = newStatus;
    if (adminNotes) data.admin_notes = adminNotes;
    try {
      await updateSupportTicket(selected.id, data);
      setSelected(null);
      load();
    } catch {}
  };

  const statusColorMap: Record<string, string> = {
    open: '#F59E0B', in_progress: '#3B82F6', resolved: '#10B981', closed: '#6B7280',
  };

  const columns: Column[] = [
    { key: 'id', header: 'ID', width: '50px' },
    { key: 'user_name', header: 'User' },
    { key: 'subject', header: 'Subject' },
    { key: 'category', header: 'Category', width: '100px' },
    {
      key: 'status', header: 'Status', width: '110px',
      render: (row) => {
        const c = statusColorMap[row.status] || colors.grayScale500;
        return <span className="px-2 py-0.5 rounded-full text-xs font-medium" style={{ backgroundColor: `${c}20`, color: c }}>{row.status.replace('_', ' ')}</span>;
      },
    },
    { key: 'created_at', header: 'Created', render: (row) => row.created_at?.split('T')[0] },
  ];

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <h1 className="text-2xl font-bold mb-6" style={{ color: colors.textColor }}>Support Tickets</h1>

      <div className="mb-4">
        <select
          value={statusFilter}
          onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
          className="px-3 py-2 rounded-xl text-sm outline-none"
          style={{ backgroundColor: colors.inputBackground, border: `1px solid ${colors.borderColor}`, color: colors.textColor }}
        >
          {statusOptions.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      </div>

      <AdminDataTable
        columns={columns}
        data={tickets}
        loading={loading}
        onRowClick={(row) => { setSelected(row); setNewStatus(row.status); setAdminNotes(row.admin_notes || ''); }}
        emptyMessage="No tickets found"
      />
      <AdminPagination page={page} totalPages={Math.ceil(total / pageSize)} onPageChange={setPage} />

      {/* Detail / Edit Panel */}
      {selected && (
        <div className="fixed inset-0 flex items-center justify-center z-[100] p-4" style={{ backgroundColor: colors.modalBackground }}>
          <div
            className="rounded-2xl p-6 w-full max-w-lg max-h-[80vh] overflow-y-auto"
            style={{ backgroundColor: colors.cardBackground || colors.backgroundColor, boxShadow: colors.shadowLarge }}
          >
            <h3 className="text-lg font-bold mb-1" style={{ color: colors.textColor }}>{selected.subject}</h3>
            <p className="text-xs mb-4" style={{ color: colors.grayScale500 }}>
              #{selected.id} | {selected.user_name} ({selected.user_email}) | {selected.category}
            </p>

            <div className="rounded-lg p-3 mb-4 text-sm" style={{ backgroundColor: colors.inputBackground, color: colors.textColor }}>
              {selected.description}
            </div>

            <div className="space-y-3 mb-4">
              <div>
                <label className="text-xs font-medium block mb-1" style={{ color: colors.grayScale500 }}>Status</label>
                <select
                  value={newStatus}
                  onChange={(e) => setNewStatus(e.target.value)}
                  className="w-full px-3 py-2 rounded-lg text-sm outline-none"
                  style={{ backgroundColor: colors.inputBackground, border: `1px solid ${colors.borderColor}`, color: colors.textColor }}
                >
                  {statusOptions.filter((o) => o.value).map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-xs font-medium block mb-1" style={{ color: colors.grayScale500 }}>Admin Notes</label>
                <textarea
                  value={adminNotes}
                  onChange={(e) => setAdminNotes(e.target.value)}
                  rows={3}
                  className="w-full px-3 py-2 rounded-lg text-sm outline-none resize-y"
                  style={{ backgroundColor: colors.inputBackground, border: `1px solid ${colors.borderColor}`, color: colors.textColor }}
                />
              </div>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => setSelected(null)}
                className="flex-1 py-2.5 rounded-xl border text-sm font-medium"
                style={{ borderColor: colors.borderColor, color: colors.textColor }}
              >
                Cancel
              </button>
              <button
                onClick={handleUpdate}
                className="flex-1 py-2.5 rounded-xl text-sm font-medium text-white"
                style={{ backgroundColor: colors.primary }}
              >
                Update Ticket
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminSupport;
